import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { alpha, lighten } from '@mui/material/styles';

import Nav from './nav';
import { BASEURL, PRIMARYCOLOR } from '../../Constants';
import { formatAttemptPolicy } from '../pentests/vishing/constants';

import GroupIcon from '@mui/icons-material/Group';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PhoneIcon from '@mui/icons-material/Phone';
import DoneIcon from '@mui/icons-material/Done';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import PhoneMissedRoundedIcon from '@mui/icons-material/PhoneMissedRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';

const PASS_GREEN = '#2E7D32';
const FAIL_RED = '#C62828';
const WARNING_AMBER = '#D97706';
const SLATE_TEXT = '#5C6B80';
const BRAND_BLUE = PRIMARYCOLOR || '#005CC5';

const getGrade = (score) => {
  if (score == null) return '?';
  if (score < 60) return 'F';
  if (score < 70) return 'D';
  if (score < 80) return 'C';
  if (score < 90) return 'B';
  if (score <= 95) return 'A-';
  return 'A+';
};

const gradePalette = {
  'A+': BRAND_BLUE,
  'A-': BRAND_BLUE,
  B: lighten(BRAND_BLUE, 0.12),
  C: WARNING_AMBER,
  D: '#EF6C00',
  F: FAIL_RED,
  '?': '#94A3B8'
};

const gradeMessages = {
  'A+': 'The campaign indicates a strong resistance to the vishing pretext.',
  'A-': 'Overall resilience was strong, with only limited susceptibility observed.',
  B: 'The campaign performed well, but there are still users who need reinforcement.',
  C: 'User awareness was mixed and should be improved through focused training.',
  D: 'The engagement exposed material gaps in resistance to voice-based social engineering.',
  F: 'The campaign shows a high degree of susceptibility and needs immediate attention.',
  '?': 'Risk posture will appear once calls are completed and scored.'
};

const EMPTY_STATS = {
  risk_score: null,
  scenarios: [],
  voices: [],
  users: [],
  total_users: 0,
  total_calls: 0,
  ended_calls: 0,
  ongoing_calls: 0,
  answered_calls: 0,
  no_answer_calls: 0,
  passed_users: 0,
  failed_users: 0
};

const surfaceCardSx = {
  height: '100%',
  borderRadius: 4,
  border: '1px solid',
  borderColor: alpha(BRAND_BLUE, 0.12),
  boxShadow: `0 12px 28px ${alpha(BRAND_BLUE, 0.06)}`,
  backgroundColor: '#ffffff'
};

const formatPercent = (value, total) => {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

const progressValue = (value, total) => {
  if (!total) return 0;
  return Math.min((value / total) * 100, 100);
};

const getInitials = (value) => {
  if (!value) return '?';
  const parts = String(value).trim().split(/\s+/).slice(0, 2);
  return parts.map((part) => part[0]?.toUpperCase() || '').join('') || '?';
};

const sectionHeadingSx = {
  fontSize: '0.78rem',
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  fontWeight: 800,
  color: BRAND_BLUE
};

const markdownComponents = {
  p: ({ children }) => (
    <Typography variant="body2" sx={{ mb: 1.4, lineHeight: 1.8, color: '#20344B' }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ my: 1.2, pl: 2.4, color: '#20344B' }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ my: 1.2, pl: 2.4, color: '#20344B' }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: 0.75, lineHeight: 1.7 }}>
      {children}
    </Box>
  ),
  strong: ({ children }) => (
    <Box component="strong" sx={{ color: '#10233B', fontWeight: 800 }}>
      {children}
    </Box>
  )
};

const MetricCard = ({ label, value, helper, Icon, accent = BRAND_BLUE }) => (
  <Card sx={{ ...surfaceCardSx, overflow: 'hidden', borderTop: `3px solid ${accent}` }}>
    <CardContent sx={{ p: 2.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ width: 48, height: 48, borderRadius: 3, backgroundColor: alpha(accent, 0.12), color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon sx={{ fontSize: 26 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: alpha('#0f172a', 0.68), letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography variant="h4" sx={{ mt: 0.4, fontWeight: 800, color: '#10233B', lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.9, color: SLATE_TEXT }}>
            {helper}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const JourneyStep = ({ label, value, helper, Icon, accent, progress }) => (
  <Grid item xs={12} sm={6} lg={3}>
    <Box sx={{ height: '100%', borderRadius: 3.5, border: '1px solid', borderColor: alpha(accent, 0.14), backgroundColor: '#ffffff', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ width: 42, height: 42, borderRadius: 3, backgroundColor: alpha(accent, 0.14), color: accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon sx={{ fontSize: 22 }} />
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#10233B', lineHeight: 1 }}>
          {value}
        </Typography>
      </Box>
      <Typography sx={{ mt: 1.5, fontSize: '0.88rem', fontWeight: 700, color: '#10233B' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, minHeight: 40, color: SLATE_TEXT }}>
        {helper}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          mt: 1.4,
          height: 8,
          borderRadius: 999,
          backgroundColor: alpha(accent, 0.14),
          '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: accent }
        }}
      />
    </Box>
  </Grid>
);

const ProfilePanel = ({ title, subtitle, icon, items, emptyState, renderMeta }) => (
  <Card sx={surfaceCardSx}>
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.8 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 3, backgroundColor: alpha(BRAND_BLUE, 0.12), color: BRAND_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#10233B' }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {items.length === 0 ? (
        <Box sx={{ p: 2, borderRadius: 3, border: '1px dashed', borderColor: alpha(BRAND_BLUE, 0.18), backgroundColor: '#ffffff' }}>
          <Typography variant="body2" sx={{ color: SLATE_TEXT }}>
            {emptyState}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          {items.map((item, index) => (
            <Box key={item.id || item.name || index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, p: 1.35, borderRadius: 3, border: '1px solid', borderColor: alpha(BRAND_BLUE, 0.1), backgroundColor: index === 0 ? alpha(BRAND_BLUE, 0.04) : '#ffffff' }}>
              {item.image ? (
                <Avatar src={item.image} sx={{ width: 40, height: 40 }} />
              ) : (
                <Avatar sx={{ width: 40, height: 40, bgcolor: alpha(BRAND_BLUE, 0.12), color: BRAND_BLUE }}>
                  {icon}
                </Avatar>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, color: '#10233B' }}>
                  {item.name || 'Unnamed'}
                </Typography>
                {renderMeta(item)}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </CardContent>
  </Card>
);

const PaginatedTable = ({ title, rows, page, setPage, accent }) => {
  const pageSize = 5;
  const pageCount = Math.ceil(rows.length / pageSize) || 1;
  const safePage = Math.min(page, pageCount - 1);
  const view = rows.slice(safePage * pageSize, safePage * pageSize + pageSize);

  useEffect(() => {
    if (page !== safePage) {
      setPage(safePage);
    }
  }, [page, safePage, setPage]);

  return (
    <Card sx={surfaceCardSx}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.25, pb: 1.5, display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: '#10233B' }}>
              {title}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.4, color: SLATE_TEXT }}>
              {rows.length} total users in this outcome group
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Page ${safePage + 1} of ${pageCount}`} sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE, fontWeight: 700 }} />
            <Button size="small" variant="outlined" onClick={() => setPage((current) => Math.max(current - 1, 0))} disabled={safePage === 0} sx={{ borderColor: alpha(BRAND_BLUE, 0.18), color: BRAND_BLUE }}>
              Previous
            </Button>
            <Button size="small" variant="contained" onClick={() => setPage((current) => Math.min(current + 1, pageCount - 1))} disabled={safePage === pageCount - 1} sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: lighten(BRAND_BLUE, 0.05) } }}>
              Next
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ mx: 2.25, mb: 2.25, borderRadius: 3, border: '1px solid', borderColor: alpha(BRAND_BLUE, 0.1), boxShadow: 'none', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: alpha(BRAND_BLUE, 0.96), color: '#ffffff', borderBottom: 'none', fontWeight: 800, letterSpacing: '0.03em' } }}>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scenario</TableCell>
                <TableCell>Voice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {view.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: SLATE_TEXT }}>
                    No users available yet.
                  </TableCell>
                </TableRow>
              ) : (
                view.map((user, index) => (
                  <TableRow key={`${user.phone}-${user.name}-${index}`} hover sx={{ '& td': { borderBottomColor: alpha(BRAND_BLUE, 0.06) }, '&:nth-of-type(odd)': { backgroundColor: alpha(BRAND_BLUE, 0.018) }, '&:hover': { backgroundColor: alpha(accent, 0.08) } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem', bgcolor: alpha(accent, 0.16), color: accent }}>
                          {getInitials(user.name)}
                        </Avatar>
                        <Typography sx={{ fontWeight: 700, color: '#10233B' }}>
                          {user.name || 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#20344B' }}>{user.phone || '-'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={user.passOrFail || 'Unknown'} sx={{ bgcolor: user.passOrFail === 'Passed' ? alpha(PASS_GREEN, 0.12) : alpha(FAIL_RED, 0.12), color: user.passOrFail === 'Passed' ? PASS_GREEN : FAIL_RED, fontWeight: 800 }} />
                    </TableCell>
                    <TableCell sx={{ color: '#20344B' }}>{user.scenario || '-'}</TableCell>
                    <TableCell sx={{ color: '#20344B' }}>{user.voice || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const VishingDashboard = () => {
  const { pentest_id } = useParams();
  const navigate = useNavigate();

  const [stats, setStats] = useState(EMPTY_STATS);
  const [summaryIdx, setSummaryIdx] = useState(0);
  const [failPage, setFailPage] = useState(0);
  const [passPage, setPassPage] = useState(0);

  useEffect(() => {
    let alive = true;

    (async () => {
      const token = sessionStorage.getItem('token');
      if (!token) {
        navigate('/');
        return;
      }

      try {
        const response = await fetch(`${BASEURL}/pentests/vishing/stats?pentest_id=${pentest_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await response.json();
        const nextStats = json?.stats || {};
        if (alive) {
          setStats({ ...EMPTY_STATS, ...nextStats });
        }
      } catch (error) {
        console.error(error);
        if (alive) {
          setStats({ ...EMPTY_STATS });
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, [pentest_id, navigate]);

  useEffect(() => {
    setSummaryIdx(0);
    setFailPage(0);
    setPassPage(0);
  }, [stats.users.length]);

  const grade = getGrade(stats.risk_score);
  const gradeColor = gradePalette[grade] || '#94A3B8';
  const gradeMessage = gradeMessages[grade] || '';

  const failUsers = useMemo(() => stats.users.filter((user) => user.passOrFail === 'Failed'), [stats.users]);
  const passUsers = useMemo(() => stats.users.filter((user) => user.passOrFail === 'Passed'), [stats.users]);

  const assessedUsers = stats.passed_users + stats.failed_users;
  const answerRate = formatPercent(stats.answered_calls, stats.total_calls);
  const noAnswerRate = formatPercent(stats.no_answer_calls, stats.total_calls);
  const completionRate = formatPercent(stats.ended_calls, stats.total_calls);
  const failRate = formatPercent(stats.failed_users, assessedUsers || stats.total_users);
  const passRate = formatPercent(stats.passed_users, assessedUsers || stats.total_users);

  const resultInsight = stats.failed_users > stats.passed_users
    ? 'More users failed than passed, which suggests the pretext was convincing enough to overcome normal skepticism.'
    : 'More users passed than failed, which suggests the audience showed decent resistance to the pretext.';

  const resultsChartData = {
    labels: ['Passed', 'Failed'],
    datasets: [
      {
        label: 'Users',
        data: [stats.passed_users, stats.failed_users],
        borderWidth: 0,
        borderRadius: 16,
        maxBarThickness: 26,
        backgroundColor: [PASS_GREEN, FAIL_RED]
      }
    ]
  };

  const resultsChartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => `${context.raw} users`
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: alpha(BRAND_BLUE, 0.08) },
        border: { display: false },
        ticks: { precision: 0, color: SLATE_TEXT }
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { color: '#10233B', font: { weight: 700 } }
      }
    }
  };

  const latest = stats.users[summaryIdx];
  const campaignStatusLabel = stats.risk_score == null ? 'Campaign in progress' : 'Scored campaign';
  const campaignStatusTone = stats.risk_score == null ? alpha(BRAND_BLUE, 0.1) : alpha(PASS_GREEN, 0.12);
  const campaignStatusColor = stats.risk_score == null ? BRAND_BLUE : PASS_GREEN;

  const primaryMetrics = [
    { label: 'Total Users', value: stats.total_users, helper: 'Audience members currently in scope for the campaign.', Icon: GroupIcon, accent: BRAND_BLUE },
    { label: 'Total Calls', value: stats.total_calls, helper: 'Completed and attempted calls executed by the campaign.', Icon: PhoneIcon, accent: BRAND_BLUE },
    { label: 'Answered Calls', value: stats.answered_calls, helper: `${answerRate} of all placed calls resulted in a live interaction.`, Icon: TaskAltRoundedIcon, accent: BRAND_BLUE },
    { label: 'Failed Users', value: stats.failed_users, helper: `${failRate} of assessed users were susceptible to the vishing pretext.`, Icon: ReportProblemRoundedIcon, accent: FAIL_RED }
  ];

  const secondaryMetrics = [
    { label: 'Passed Users', value: stats.passed_users, helper: `${passRate} of assessed users resisted the social engineering attempt.`, Icon: CheckCircleIcon, accent: PASS_GREEN },
    { label: 'Ended Calls', value: stats.ended_calls, helper: `${completionRate} of placed calls were fully completed.`, Icon: DoneIcon, accent: BRAND_BLUE },
    { label: 'Ongoing Calls', value: stats.ongoing_calls, helper: 'Calls still in progress and awaiting a final disposition.', Icon: HourglassEmptyIcon, accent: WARNING_AMBER },
    { label: 'No Answer Calls', value: stats.no_answer_calls, helper: `${noAnswerRate} of placed calls did not result in a live connection.`, Icon: PhoneMissedRoundedIcon, accent: '#6B7280' }
  ];

  const journeySteps = [
    { label: 'Users in Scope', value: stats.total_users, helper: 'Target employees scheduled for campaign contact.', Icon: GroupIcon, accent: BRAND_BLUE, progress: progressValue(stats.total_users, stats.total_users || 1) },
    { label: 'Calls Placed', value: stats.total_calls, helper: `${formatPercent(stats.total_calls, stats.total_users || stats.total_calls)} of the target audience has been contacted.`, Icon: PhoneIcon, accent: BRAND_BLUE, progress: progressValue(stats.total_calls, stats.total_users || stats.total_calls || 1) },
    { label: 'Answered', value: stats.answered_calls, helper: `${answerRate} of call attempts resulted in a live conversation.`, Icon: TaskAltRoundedIcon, accent: BRAND_BLUE, progress: progressValue(stats.answered_calls, stats.total_calls || 1) },
    { label: 'Assessed', value: assessedUsers, helper: 'Users with a completed pass or fail outcome recorded.', Icon: InsightsRoundedIcon, accent: BRAND_BLUE, progress: progressValue(assessedUsers, stats.answered_calls || stats.total_calls || 1) }
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
      <Nav />
      <Box sx={{ p: { xs: 2, md: 3 }, flexGrow: 1 }}>
        <Box sx={{ mb: 3.2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography sx={sectionHeadingSx}>Social Engineering Dashboard</Typography>
            <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
              Vishing Pentest Dashboard
            </Typography>
            <Typography variant="body1" sx={{ mt: 1.1, maxWidth: 760, color: SLATE_TEXT, lineHeight: 1.75 }}>
              Track campaign exposure, answer rates, and the users who were most susceptible to voice-based social engineering.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip label={campaignStatusLabel} sx={{ bgcolor: campaignStatusTone, color: campaignStatusColor, fontWeight: 800 }} />
            <Chip label={`${stats.scenarios.length} scenario${stats.scenarios.length === 1 ? '' : 's'}`} sx={{ bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE, fontWeight: 700 }} />
            <Chip label={`${stats.voices.length} voice${stats.voices.length === 1 ? '' : 's'}`} sx={{ bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE, fontWeight: 700 }} />
          </Box>
        </Box>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={4} lg={3}>
            <Card sx={{ ...surfaceCardSx, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, backgroundColor: gradeColor }} />
              <CardContent sx={{ p: 2.6, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Typography sx={sectionHeadingSx}>Risk Posture</Typography>
                <Typography variant="h1" sx={{ mt: 1.8, fontSize: { xs: '4.5rem', sm: '5.5rem' }, lineHeight: 1, fontWeight: 900, color: gradeColor }}>
                  {grade}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1.2, fontWeight: 700, color: '#10233B' }}>
                  Voice susceptibility score
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, color: SLATE_TEXT, lineHeight: 1.8 }}>
                  {gradeMessage}
                </Typography>
                <Box sx={{ mt: 1.8, pt: 1.6, display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: 3, backgroundColor: alpha(gradeColor, 0.14), color: gradeColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <TimelineRoundedIcon />
                  </Box>
                  <Typography variant="body2" sx={{ color: SLATE_TEXT, lineHeight: 1.65 }}>
                    {stats.risk_score == null ? 'Scores will populate as the campaign completes.' : `${stats.risk_score} / 100 risk score recorded for the current engagement.`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Card sx={surfaceCardSx}>
              <CardContent sx={{ p: 2.6 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography sx={sectionHeadingSx}>Campaign Snapshot</Typography>
                    <Typography variant="h5" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>
                      High-level view of vishing performance
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.8, maxWidth: 620, color: SLATE_TEXT, lineHeight: 1.8 }}>
                      This panel highlights how effectively the campaign reached users, how often the pretext converted into a real interaction, and where the most meaningful exposure sits right now.
                    </Typography>
                  </Box>
                  <Chip icon={<AutoAwesomeRoundedIcon />} label={stats.failed_users > stats.passed_users ? 'Elevated user susceptibility' : 'User resistance trending stronger'} sx={{ bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE, fontWeight: 800, px: 0.75 }} />
                </Box>

                <Grid container spacing={2} sx={{ mt: 0.4 }}>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.7, borderRadius: 3, backgroundColor: '#ffffff', border: '1px solid', borderColor: alpha(BRAND_BLUE, 0.12), borderLeft: `4px solid ${BRAND_BLUE}` }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>Answer Rate</Typography>
                      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>{answerRate}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>{stats.answered_calls} answered calls out of {stats.total_calls} attempts.</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.7, borderRadius: 3, backgroundColor: '#ffffff', border: '1px solid', borderColor: alpha(FAIL_RED, 0.12), borderLeft: `4px solid ${FAIL_RED}` }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: FAIL_RED }}>Failure Rate</Typography>
                      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>{failRate}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>{stats.failed_users} users failed once an outcome was recorded.</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ p: 1.7, borderRadius: 3, backgroundColor: '#ffffff', border: '1px solid', borderColor: alpha(PASS_GREEN, 0.12), borderLeft: `4px solid ${PASS_GREEN}` }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PASS_GREEN }}>Completion Rate</Typography>
                      <Typography variant="h4" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>{completionRate}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT }}>{stats.ended_calls} calls have reached a final disposition.</Typography>
                    </Box>
                  </Grid>
                </Grid>
                <Box sx={{ mt: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mb: 0.8, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#10233B' }}>Call completion progress</Typography>
                    <Typography variant="body2" sx={{ color: SLATE_TEXT }}>{stats.ended_calls} of {stats.total_calls} calls ended</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressValue(stats.ended_calls, stats.total_calls || 1)}
                    sx={{ height: 10, borderRadius: 999, backgroundColor: alpha(BRAND_BLUE, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: BRAND_BLUE } }}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, mb: 0.8, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#10233B' }}>Susceptibility among assessed users</Typography>
                    <Typography variant="body2" sx={{ color: SLATE_TEXT }}>{stats.failed_users} failed / {assessedUsers} assessed users</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={progressValue(stats.failed_users, assessedUsers || 1)}
                    sx={{ height: 10, borderRadius: 999, backgroundColor: alpha(FAIL_RED, 0.1), '& .MuiLinearProgress-bar': { borderRadius: 999, backgroundColor: FAIL_RED } }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} md={6}>
            <ProfilePanel
              title="Campaign Scenarios"
              subtitle="Pretexts and narratives used to drive user engagement."
              icon={<CampaignRoundedIcon />}
              items={stats.scenarios}
              emptyState="No scenarios have been configured yet."
              renderMeta={(scenario) => (
                <Typography variant="body2" sx={{ mt: 0.4, color: SLATE_TEXT, lineHeight: 1.7 }}>
                  {scenario.description || 'No scenario description provided.'}
                </Typography>
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <ProfilePanel
              title="Voice Personas"
              subtitle="Synthetic voice profiles used during the engagement."
              icon={<RecordVoiceOverRoundedIcon />}
              items={stats.voices}
              emptyState="No voices have been configured yet."
              renderMeta={(voice) => (
                <Box sx={{ mt: 0.55 }}>
                  <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 0.8 }}>
                    {voice.language ? <Chip size="small" label={voice.language} sx={{ bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE, fontWeight: 700 }} /> : null}
                    {voice.gender ? <Chip size="small" label={voice.gender} sx={{ bgcolor: alpha(BRAND_BLUE, 0.08), color: BRAND_BLUE, fontWeight: 700 }} /> : null}
                  </Box>
                  <Typography variant="body2" sx={{ color: SLATE_TEXT, lineHeight: 1.7 }}>
                    {voice.description || 'No voice description provided.'}
                  </Typography>
                </Box>
              )}
            />
          </Grid>
        </Grid>

        <Box sx={{ mb: 1.5 }}>
          <Typography sx={sectionHeadingSx}>Campaign Metrics</Typography>
          <Typography variant="h5" sx={{ mt: 0.65, fontWeight: 800, color: '#10233B' }}>
            Operational performance at a glance
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          {primaryMetrics.map((metric) => (
            <Grid key={metric.label} item xs={12} sm={6} lg={3}>
              <MetricCard {...metric} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {secondaryMetrics.map((metric) => (
            <Grid key={metric.label} item xs={12} sm={6} lg={3}>
              <MetricCard {...metric} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
          <Grid item xs={12} lg={7}>
            <Card sx={surfaceCardSx}>
              <CardContent sx={{ p: 2.6 }}>
                <Typography sx={sectionHeadingSx}>Interaction Funnel</Typography>
                <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                  How users moved through the vishing campaign
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.8 }}>
                  This journey highlights the transition from targeted users to live calls and finally to assessed outcomes.
                </Typography>

                <Grid container spacing={1.4} sx={{ mt: 1.2 }}>
                  {journeySteps.map((step) => (
                    <JourneyStep key={step.label} {...step} />
                  ))}
                </Grid>

                <Divider sx={{ my: 2.5, borderColor: alpha(BRAND_BLUE, 0.1) }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: alpha(PASS_GREEN, 0.14), backgroundColor: '#ffffff', borderLeft: `4px solid ${PASS_GREEN}`, height: '100%' }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PASS_GREEN }}>Resistant Users</Typography>
                      <Typography variant="h4" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>{stats.passed_users}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT, lineHeight: 1.75 }}>
                        {passRate} of assessed users resisted the call and maintained a stronger security posture.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: alpha(FAIL_RED, 0.14), backgroundColor: '#ffffff', borderLeft: `4px solid ${FAIL_RED}`, height: '100%' }}>
                      <Typography sx={{ fontSize: '0.76rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: FAIL_RED }}>Susceptible Users</Typography>
                      <Typography variant="h4" sx={{ mt: 0.8, fontWeight: 800, color: '#10233B' }}>{stats.failed_users}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.65, color: SLATE_TEXT, lineHeight: 1.75 }}>
                        {failRate} of assessed users engaged with the pretext strongly enough to fail the test.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Card sx={surfaceCardSx}>
              <CardContent sx={{ p: 2.6 }}>
                <Typography sx={sectionHeadingSx}>Outcome Breakdown</Typography>
                <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                  Pass versus fail results
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.85, color: SLATE_TEXT, lineHeight: 1.8 }}>
                  A compact view of campaign outcomes with color-coded pass and fail counts.
                </Typography>

                <Grid container spacing={1.5} sx={{ mt: 0.8, mb: 2.1 }}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid', borderColor: alpha(PASS_GREEN, 0.12), borderLeft: `4px solid ${PASS_GREEN}` }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: PASS_GREEN }}>Passed</Typography>
                      <Typography variant="h4" sx={{ mt: 0.7, fontWeight: 800, color: '#10233B' }}>{stats.passed_users}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid', borderColor: alpha(FAIL_RED, 0.12), borderLeft: `4px solid ${FAIL_RED}` }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: FAIL_RED }}>Failed</Typography>
                      <Typography variant="h4" sx={{ mt: 0.7, fontWeight: 800, color: '#10233B' }}>{stats.failed_users}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Box sx={{ height: 220 }}>
                  <Bar data={resultsChartData} options={resultsChartOptions} />
                </Box>

                <Box sx={{ mt: 2, p: 1.6, borderRadius: 3, bgcolor: '#ffffff', border: '1px solid', borderColor: alpha(BRAND_BLUE, 0.1) }}>
                  <Typography sx={{ fontWeight: 700, color: '#10233B' }}>Analyst takeaway</Typography>
                  <Typography variant="body2" sx={{ mt: 0.7, color: SLATE_TEXT, lineHeight: 1.75 }}>
                    {resultInsight}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ ...surfaceCardSx, mb: 2.5 }}>
          <CardContent sx={{ p: 2.6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box>
                <Typography sx={sectionHeadingSx}>Call Intelligence</Typography>
                <Typography variant="h5" sx={{ mt: 0.75, fontWeight: 800, color: '#10233B' }}>
                  Latest call summary
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={stats.users.length ? `${summaryIdx + 1} of ${stats.users.length}` : 'No call summaries'} sx={{ bgcolor: alpha(BRAND_BLUE, 0.1), color: BRAND_BLUE, fontWeight: 700 }} />
                <Button size="small" variant="outlined" onClick={() => setSummaryIdx((current) => Math.max(current - 1, 0))} disabled={summaryIdx === 0} sx={{ borderColor: alpha(BRAND_BLUE, 0.18), color: BRAND_BLUE }}>
                  Previous
                </Button>
                <Button size="small" variant="contained" onClick={() => setSummaryIdx((current) => Math.min(current + 1, stats.users.length - 1))} disabled={stats.users.length === 0 || summaryIdx === stats.users.length - 1} sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: lighten(BRAND_BLUE, 0.05) } }}>
                  Next
                </Button>
              </Box>
            </Box>

            {latest ? (
              <Grid container spacing={2.25} sx={{ mt: 0.6 }}>
                <Grid item xs={12} md={4}>
                  <Box sx={{ p: 2, borderRadius: 3.5, border: '1px solid', borderColor: alpha(BRAND_BLUE, 0.12), bgcolor: '#ffffff', height: '100%' }}>
                    <Typography sx={sectionHeadingSx}>Call Subject</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mt: 1.4 }}>
                      <Avatar sx={{ width: 46, height: 46, bgcolor: alpha(BRAND_BLUE, 0.12), color: BRAND_BLUE }}>
                        {getInitials(latest.name)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#10233B' }}>{latest.name || 'Unknown user'}</Typography>
                        <Typography variant="body2" sx={{ color: SLATE_TEXT }}>{latest.phone || 'No phone on record'}</Typography>
                      </Box>
                    </Box>

                    <Chip label={latest.passOrFail || 'Unknown'} sx={{ mt: 1.5, bgcolor: latest.passOrFail === 'Passed' ? alpha(PASS_GREEN, 0.12) : alpha(FAIL_RED, 0.12), color: latest.passOrFail === 'Passed' ? PASS_GREEN : FAIL_RED, fontWeight: 800 }} />

                    <Divider sx={{ my: 1.8, borderColor: alpha(BRAND_BLUE, 0.1) }} />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>Scenario</Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: '#20344B' }}>{latest.scenario || 'Not specified'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>Voice</Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: '#20344B' }}>{latest.voice || 'Not specified'}</Typography>
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: BRAND_BLUE }}>Maximum Redials</Typography>
                        <Typography variant="body2" sx={{ mt: 0.45, color: '#20344B' }}>{formatAttemptPolicy(latest.max_redials)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Box sx={{ p: 2.1, borderRadius: 3.5, border: '1px solid', borderColor: alpha(BRAND_BLUE, 0.12), backgroundColor: '#ffffff', minHeight: 280 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                      <Box sx={{ width: 38, height: 38, borderRadius: 3, bgcolor: alpha(BRAND_BLUE, 0.12), color: BRAND_BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <InsightsRoundedIcon />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, color: '#10233B' }}>Analyst call recap</Typography>
                        <Typography variant="body2" sx={{ color: SLATE_TEXT }}>Narrative summary of the most recently selected call.</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 0.8, '&::-webkit-scrollbar': { width: 8 }, '&::-webkit-scrollbar-thumb': { backgroundColor: alpha(BRAND_BLUE, 0.2), borderRadius: 999 } }}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {latest.summary || 'No summary provided for this call.'}
                      </ReactMarkdown>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            ) : (
              <Box sx={{ mt: 1.6, p: 2.2, borderRadius: 3, border: '1px dashed', borderColor: alpha(BRAND_BLUE, 0.16), backgroundColor: '#ffffff' }}>
                <Typography variant="body2" sx={{ color: SLATE_TEXT }}>No call details are available yet.</Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mb: 1.5 }}>
          <Typography sx={sectionHeadingSx}>User Outcomes</Typography>
          <Typography variant="h5" sx={{ mt: 0.65, fontWeight: 800, color: '#10233B' }}>
            Review who resisted and who was susceptible
          </Typography>
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={6}>
            <PaginatedTable title="Users Who Failed" rows={failUsers} page={failPage} setPage={setFailPage} accent={FAIL_RED} />
          </Grid>
          <Grid item xs={12} lg={6}>
            <PaginatedTable title="Users Who Passed" rows={passUsers} page={passPage} setPage={setPassPage} accent={PASS_GREEN} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default VishingDashboard;
